import { ImageOff } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { PlainTextWithCollapsedQuotes } from '../SalesLeads/PlainTextWithCollapsedQuotes'

/**
 * Blocks every remote subresource while still letting inline styles and data:
 * images through, so the email lays out roughly right.
 *
 * A `<meta http-equiv>` CSP is a standard delivery mechanism and applies to the
 * iframe document, unlike the `csp` iframe attribute (Chrome-only).
 */
const BLOCK_REMOTE_CSP = "default-src 'none'; img-src data:; style-src 'unsafe-inline' data:; font-src data:"

/**
 * Anything the browser would *fetch*: a loading attribute, a CSS `url()`, or a
 * remote stylesheet. A plain `<a href="https://…">` loads nothing, so linky mail
 * — most of what we send — no longer claims its images are being blocked.
 *
 * The http(s) can sit anywhere inside the attribute value, not just at its
 * start: a `srcset` lists candidates, and a remote one behind a leading `data:`
 * candidate is still fetched. Missing it would block the image with no way to
 * reveal it. `data-src` is excluded — a lazy-load attribute loads nothing.
 */
const REMOTE_REF =
  /(?<![-\w])(?:src|srcset|poster|background)\s*=\s*(?:["'][^"']*)?https?:|url\(\s*["']?https?:|<link\b[^>]*href\s*=\s*["']?https?:/i

function wrap(bodyHtml: string, allowRemote: boolean): string {
  const csp = allowRemote ? '' : `<meta http-equiv="Content-Security-Policy" content="${BLOCK_REMOTE_CSP}">`
  // The email's own <html>/<head> tags, if any, get merged into this document
  // by the parser — the CSP still lands in head and is applied first.
  return `<!doctype html><html><head>${csp}</head><body>${bodyHtml}</body></html>`
}

interface Props {
  id: string
  subject: string
  bodyHtml: string
  bodyText: string
}

/**
 * HTML bodies render inside a sandboxed iframe so a marketing template's CSS
 * can't leak into the admin UI, sized to its content on load. Plain-text bodies
 * fall back to the quote-collapsing renderer used on the lead activity feed.
 *
 * Remote images are blocked until asked for: an inbound body can carry a
 * tracking pixel, and browsing this page shouldn't tell every sender the moment
 * a staff member read their mail, nor hand them staff IPs. Same opt-in Gmail
 * uses. Our own open pixel is stripped server-side, so "Show images" can't
 * record a staff read as the recipient opening our mail.
 */
export function EmailMessageBody({ id, subject, bodyHtml, bodyText }: Props) {
  const [allowRemote, setAllowRemote] = useState(false)

  if (!bodyHtml) {
    return <PlainTextWithCollapsedQuotes text={bodyText} />
  }

  const hasRemote = REMOTE_REF.test(bodyHtml)

  return (
    <>
      {hasRemote && !allowRemote && (
        <div className="text-muted-foreground bg-muted/40 mt-2 flex items-center gap-2 rounded border px-3 py-1.5 text-xs">
          <ImageOff className="size-3.5 shrink-0" />
          <span>Remote images blocked — loading them tells the sender you opened this.</span>
          <Button variant="link" size="sm" className="h-auto p-0" onClick={() => setAllowRemote(true)}>
            Show images
          </Button>
        </div>
      )}
      <iframe
        // Re-mount on toggle: srcDoc alone would reuse the CSP-bound document.
        key={`${id}-${allowRemote}`}
        srcDoc={wrap(bodyHtml, allowRemote)}
        title={subject || 'Email'}
        className="mt-2 w-full rounded border-0"
        style={{ minHeight: 120 }}
        onLoad={(event) => {
          const frame = event.currentTarget
          if (frame.contentDocument) {
            frame.contentDocument.body.style.fontFamily =
              '"Google Sans",Roboto,RobotoDraft,Helvetica,Arial,sans-serif'
            frame.style.height = `${frame.contentDocument.documentElement.scrollHeight}px`
          }
        }}
        sandbox="allow-same-origin"
      />
    </>
  )
}
